import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/shared/utils/cn';
import { Spinner } from '@/shared/components/ui/spinner';

const buttonVariants = cva(
  'inline-flex items-center justify-center cursor-pointer gap-2 whitespace-nowrap rounded-xl text-sm font-medium md:transition-[background-color,border-color,box-shadow] disabled:pointer-events-none disabled:opacity-50 outline-none [&_svg]:pointer-events-none [&_svg:not([class*="size-"])]:size-4 [&_svg]:shrink-0 shrink-0',
  {
    variants: {
      variant: {
        default:
          'mb-1 bg-purple-brand-600 raised raised-purple-brand-700 text-cold-200 hover:bg-purple-brand-500 hover:raised-1.5 focus-visible:shadow-focus-default',
        secondary:
          'mb-1 border-2 border-purple-brand-600 bg-cold-950 raised raised-purple-brand-600 text-secondary-foreground hover:bg-cold-800 focus-visible:shadow-focus-default',
        positive:
          'mb-1 bg-positive-700 raised raised-positive-800 text-cold-200 hover:bg-positive-600 hover:raised-1.5 focus-visible:shadow-focus-default',
        destructive:
          'mb-1 bg-white raised raised-white/50 text-cold-950 hover:bg-cold-100 focus-visible:bg-cold-100 focus-visible:shadow-focus-default',
        danger:
          'mb-1 bg-danger-600 raised raised-danger-700 text-cold-200 hover:bg-danger-500 hover:raised-1.5 focus-visible:shadow-focus-default',
        riot: 'mb-1 bg-danger-600 raised raised-danger-700 text-cold-200 hover:bg-danger-500 hover:raised-1.5 focus-visible:shadow-focus-default [&]:font-bold',
        outline:
          'mb-1 border-2 border-cold-600 bg-cold-950 raised raised-cold-600 text-foreground hover:bg-cold-800 focus-visible:border focus-visible:border-[var(--focus)] focus-visible:bg-[rgba(255,255,255,0.05)] focus-visible:shadow-focus-default',
        outlinePositive:
          'mb-1 border-2 border-positive-600 bg-cold-950 raised raised-positive-600 text-foreground hover:bg-positive-950 focus-visible:border focus-visible:border-[var(--focus)] focus-visible:bg-positive-950 focus-visible:shadow-focus-default',
        ghost: 'bg-transparent text-foreground',
        link: 'bg-transparent text-foreground',
      },
      size: {
        sm: 'h-8 py-2 px-4 gap-1.5 text-xs font-medium leading-4',
        default: 'h-9 py-2 px-4 gap-1.5 text-sm font-semibold leading-5',
        lg: 'h-10 py-2 px-4 gap-1.5 text-base font-bold leading-6',
        icon: 'h-9 py-2 px-4 text-base',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ComponentProps<'button'>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

function Button({
  className,
  variant,
  size,
  asChild = false,
  loading = false,
  disabled,
  children,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : 'button';

  const content = asChild ? (
    children
  ) : loading ? (
    <>
      <Spinner />
      {children}
    </>
  ) : (
    children
  );

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      disabled={disabled || loading}
      {...props}
    >
      {content}
    </Comp>
  );
}

export { Button, buttonVariants };
